import { useState, useEffect } from "react";
import { Plus, Edit, Settings, Trash2, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase/config";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import ContentLoader from "../authentication/ContentLoader";

// Error Boundary Component

// Declare process.env types
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      NODE_ENV: "development" | "production" | "test";
    }
  }
}

interface GradeBoundary {
  grade: string;
  min: number;
  max: number;
}

interface GradeScheme {
  id: string;
  name: string;
  type: "percentage";
  boundaries: GradeBoundary[];
  passThreshold: number;
  isActive: boolean;
  schoolID: string;
  submittedBy: string;
  timestamp: string;
  description?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}


const getGradeColor = (grade: string): string => {
  const upperGrade = grade.toUpperCase();
  // CBC Colors - Check for both code and full text match
  if (upperGrade.includes('EE') || upperGrade.includes('EXCEEDING')) return 'bg-green-600 text-white'; // Exceeding Expectations
  if (upperGrade.includes('ME') || upperGrade.includes('MEETING')) return 'bg-blue-600 text-white';  // Meeting Expectations
  if (upperGrade.includes('AE') || upperGrade.includes('APPROACHING')) return 'bg-yellow-500 text-black'; // Approaching Expectations
  if (upperGrade.includes('BE') || upperGrade.includes('BELOW')) return 'bg-red-500 text-white';    // Below Expectations

  // 8-4-4 Colors
  if (upperGrade.startsWith('A')) return 'bg-green-500 text-white';
  if (upperGrade.startsWith('B')) return 'bg-blue-500 text-white';
  if (upperGrade.startsWith('C')) return 'bg-yellow-500 text-black';
  if (upperGrade.startsWith('D')) return 'bg-orange-500 text-white';
  return 'bg-red-500 text-white';
};

const GradeSchemeForm = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  editingScheme,
  schoolID,
  submittedBy 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSave: (scheme: Omit<GradeScheme, 'id'>) => void;
  onDelete?: (id: string) => void;
  editingScheme?: GradeScheme;
  schoolID: string;
  submittedBy: string;
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<Omit<GradeScheme, 'id'>>({
    name: editingScheme?.name || "",
    type: "percentage",
    boundaries: editingScheme?.boundaries || [{ grade: "A", min: 90, max: 100 }],
    passThreshold: editingScheme?.passThreshold || 60,
    isActive: editingScheme?.isActive || false,
    description: editingScheme?.description || "",
    schoolID,
    submittedBy,
    timestamp: new Date().toISOString(),
  });

  useEffect(() => {
    if (editingScheme) {
      setFormData({
        name: editingScheme.name,
        type: "percentage",
        boundaries: editingScheme.boundaries,
        passThreshold: editingScheme.passThreshold,
        isActive: editingScheme.isActive,
        description: editingScheme.description || "",
        schoolID,
        submittedBy,
        timestamp: new Date().toISOString(),
      });
    } else {
      setFormData({
        name: "",
        type: "percentage",
        boundaries: [{ grade: "A", min: 90, max: 100 }],
        passThreshold: 60,
        isActive: false,
        description: "",
        schoolID,
        submittedBy,
        timestamp: new Date().toISOString(),
      });
    }
  }, [isOpen, editingScheme, schoolID, submittedBy]);

  const addBoundary = () => {
    setFormData(prev => ({
      ...prev,
      boundaries: [...prev.boundaries, { grade: "", min: 0, max: 100 }]
    }));
  };

  const removeBoundary = (index: number) => {
    setFormData(prev => ({
      ...prev,
      boundaries: prev.boundaries.filter((_, i) => i !== index)
    }));
  };

  const updateBoundary = (index: number, field: keyof GradeBoundary, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      boundaries: prev.boundaries.map((boundary, i) => 
        i === index ? { ...boundary, [field]: value } : boundary
      )
    }));
  };

  const autoFill844 = () => {
    const kenyanBoundaries: GradeBoundary[] = [
      { grade: "A", min: 80, max: 100 },
      { grade: "A-", min: 75, max: 79 },
      { grade: "B+", min: 70, max: 74 },
      { grade: "B", min: 65, max: 69 },
      { grade: "B-", min: 60, max: 64 },
      { grade: "C+", min: 55, max: 59 },
      { grade: "C", min: 50, max: 54 },
      { grade: "C-", min: 45, max: 49 },
      { grade: "D+", min: 40, max: 44 },
      { grade: "D", min: 35, max: 39 },
      { grade: "D-", min: 30, max: 34 },
      { grade: "E", min: 0, max: 29 },
    ];
    setFormData(prev => ({ ...prev, boundaries: kenyanBoundaries, passThreshold: 35 }));
    toast({
      title: "Auto-filled (8-4-4)",
      description: "Kenyan 8-4-4 grading system loaded.",
      duration: 3000,
    });
  };

  const autoFillCBC = () => {
    // Official KNEC/KICD descriptors with full text while preserving the code structure
    const cbcBoundaries: GradeBoundary[] = [
      { grade: "EE (Exceeding Expectations)", min: 80, max: 100 }, 
      { grade: "ME (Meeting Expectations)", min: 65, max: 79 },  
      { grade: "AE (Approaching Expectations)", min: 50, max: 64 },  
      { grade: "BE (Below Expectations)", min: 0, max: 49 },   
    ];
    setFormData(prev => ({ ...prev, boundaries: cbcBoundaries, passThreshold: 50 }));
    toast({
      title: "Auto-filled (CBC)",
      description: "Kenyan CBC grading system loaded with full descriptors.",
      duration: 3000,
    });
  };

  const handleSave = async () => {
    if (!formData.name || formData.boundaries.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Validate boundaries don't overlap
    const sortedBoundaries = [...formData.boundaries].sort((a, b) => b.min - a.min);
    for (let i = 0; i < sortedBoundaries.length - 1; i++) {
      if (sortedBoundaries[i].min <= sortedBoundaries[i + 1].max) {
        toast({
          title: "Validation Error",
          description: "Grade boundaries cannot overlap",
          variant: "destructive"
        });
        return;
      }
    }

    // Check for duplicate name if creating new
    if (!editingScheme) {
      try {
        const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
        const nameSnapshot = await getDocs(query(schemesCollection, where("name", "==", formData.name)));
        if (!nameSnapshot.empty) {
          toast({
            title: "Validation Error",
            description: "A scheme with this name already exists.",
            variant: "destructive"
          });
          return;
        }
      } catch (error) {
        console.error("Duplicate check error:", error);
        toast({
          title: "Error",
          description: "Failed to validate scheme name.",
          variant: "destructive"
        });
        return;
      }
    }

    onSave(formData);
    onClose();
    toast({
      title: "Success",
      description: `Grade scheme ${editingScheme ? 'updated' : 'created'} successfully`
    });
  };

  const handleDelete = () => {
    if (editingScheme && onDelete) {
      onDelete(editingScheme.id);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle>{editingScheme ? 'Edit' : 'Create'} Grading Scheme</DialogTitle>
            {!editingScheme && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline">
                    Auto-fill <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={autoFill844}>
                    Kenyan 8-4-4 System
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={autoFillCBC}>
                    Kenyan CBC System
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <DialogDescription>Define a grading structure for your school</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2 m-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <div className="space-y-2">
              <Label>Scheme Name</Label>
              <Input 
                placeholder="e.g., 7-Grade Scale" 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] p-3 rounded-md">
                <span className="text-sm">Percentage</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center mb-3">
              <Label>Grade Boundaries</Label>
              <Button size="sm" onClick={addBoundary}>
                <Plus className="w-3 h-3 mr-1" />
                Add Grade
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {formData.boundaries.map((boundary, index) => (
                <div key={index} className="grid grid-cols-4 gap-2 p-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] rounded">
                  <Input 
                    placeholder="Grade" 
                    value={boundary.grade}
                    onChange={(e) => updateBoundary(index, 'grade', e.target.value)}
                    className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                  />
                  <Input 
                    type="number" 
                    placeholder="Min" 
                    value={boundary.min}
                    onChange={(e) => updateBoundary(index, 'min', parseInt(e.target.value) || 0)}
                    className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                  />
                  <Input 
                    type="number" 
                    placeholder="Max" 
                    value={boundary.max}
                    onChange={(e) => updateBoundary(index, 'max', parseInt(e.target.value) || 100)}
                    className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                  />
                  <Button size="sm" variant="outline" onClick={() => removeBoundary(index)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Pass Threshold (%)</Label>
            <Input 
              type="number" 
              value={formData.passThreshold}
              onChange={(e) => setFormData(prev => ({ ...prev, passThreshold: parseInt(e.target.value) || 60 }))}
              className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea 
              placeholder="Optional description for this grading scheme..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] min-h-[60px]"
            />
          </div>

          <div className="flex gap-4">
            {editingScheme && (
              <Button variant="destructive" onClick={handleDelete} className="flex-1">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Scheme
              </Button>
            )}
            <Button onClick={handleSave} className="flex-1 bg-[#3879E3] hover:bg-[#306ECD] text-white">
              {editingScheme ? 'Update' : 'Create'} Scheme
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GradingStructure = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [gradeSchemes, setGradeSchemes] = useState<GradeScheme[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState<GradeScheme | undefined>();
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [schoolID, setSchoolID] = useState("");
  const [submittedBy, setSubmittedBy] = useState("");

  // Fetch grade schemes
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchData = async () => {
      try {
        const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
        const schemesSnapshot = await getDocs(schemesCollection);
        const schemesData = schemesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as GradeScheme[];

        if (process.env.NODE_ENV !== "production") {
          console.log("Fetched grade schemes:", schemesData);
        }

        setGradeSchemes(schemesData);
      } catch (error) {
        console.error("Error fetching grade schemes:", error);
        toast({
          title: "Data Fetch Failed",
          description: "Unable to load grading schemes.",
          variant: "destructive",
          duration: 5000,
        });
      }
    };

    fetchData();
  }, [schoolID, hasValidClaims, toast]);

  // Authentication and claims verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to manage grading schemes.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        setIsAuthLoading(false);
        return;
      }

      setSubmittedBy(user.uid);
      try {
        const token = await getIdTokenResult(user, true);
        const claims = token.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        if (process.env.NODE_ENV !== "production") {
          console.log("Claims:", { affiliations, defaultAff, claimSchoolID });
        }

        const adminAff =
          defaultAff?.role === "admin" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find((aff) => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          setSchoolID(adminAff.schoolID!);
          setHasValidClaims(true);
        } else {
          if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const retryToken = await getIdTokenResult(user, true);
          const retryClaims = retryToken.claims as CustomClaims;
          const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
          const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;
          const retrySchoolID: string | undefined = retryClaims.schoolID;

          const retryAdminAff =
            retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID);

          if (retryAdminAff && retryAdminAff.schoolID === retrySchoolID) {
            setSchoolID(retryAdminAff.schoolID!);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        toast({
          title: "Missing Permissions",
          description: "You lack permissions to manage grading schemes.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing auth");
      unsubscribe();
    };
  }, [toast, navigate]);

  const handleSaveScheme = async (schemeData: Omit<GradeScheme, 'id'>) => {
    try {
      const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
      const schemeId = editingScheme?.id || doc(schemesCollection).id;

      const schemeDataWithId: GradeScheme = {
        ...schemeData,
        id: schemeId,
        timestamp: new Date().toISOString(),
      };

      if (process.env.NODE_ENV !== "production") {
        console.log("Saving grade scheme:", schemeDataWithId);
      }

      await setDoc(doc(db, `schools/${schoolID}/gradingSchemes`, schemeId), schemeDataWithId);

      // Update local state
      if (editingScheme) {
        setGradeSchemes(prev => 
          prev.map(scheme => 
            scheme.id === editingScheme.id 
              ? schemeDataWithId
              : scheme
          )
        );
      } else {
        setGradeSchemes(prev => [...prev, schemeDataWithId]);
      }

      setEditingScheme(undefined);
      setIsFormOpen(false);
    } catch (error: any) {
      console.error("Save error:", error);
      toast({
        title: "Save Failed",
        description: error.message || "Unable to save grade scheme.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleDeleteScheme = async (schemeId: string) => {
    try {
      await deleteDoc(doc(db, `schools/${schoolID}/gradingSchemes`, schemeId));
      setGradeSchemes((prev) => prev.filter((scheme) => scheme.id !== schemeId));
      toast({
        title: "Scheme Deleted",
        description: "The grading scheme was successfully deleted.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Delete error:", error);
      toast({
        title: "Deletion Failed",
        description: error.message || "Unable to delete scheme.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const toggleActiveScheme = async (schemeId: string) => {
    const scheme = gradeSchemes.find(s => s.id === schemeId);
    if (!scheme) return;

    const newActiveState = !scheme.isActive;

    // Optimistically update local state
    setGradeSchemes(prev => 
      prev.map(s => ({
        ...s,
        isActive: s.id === schemeId ? newActiveState : (newActiveState ? false : s.isActive)
      }))
    );

    try {
      if (newActiveState) {
        // Deactivate all others
        const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
        const schemesSnapshot = await getDocs(schemesCollection);
        const updates = schemesSnapshot.docs
          .filter(doc => doc.id !== schemeId)
          .map((doc) => setDoc(doc.ref, { isActive: false }, { merge: true }));
        await Promise.all(updates);

        // Activate the selected one
        await setDoc(
          doc(db, `schools/${schoolID}/gradingSchemes`, schemeId),
          { isActive: true, timestamp: new Date().toISOString() },
          { merge: true }
        );

        toast({
          title: "Scheme Updated",
          description: `${scheme.name} is now active`,
          className: "border-green-500 bg-green-50 text-green-700 dark:border-green-400 dark:bg-green-950 dark:text-green-300",
          duration: 3000,
        });
      } else {
        // Deactivate the selected one
        await setDoc(
          doc(db, `schools/${schoolID}/gradingSchemes`, schemeId),
          { isActive: false, timestamp: new Date().toISOString() },
          { merge: true }
        );

        toast({
          title: "Scheme Updated",
          description: `${scheme.name} is now inactive`,
          variant: "destructive",
          className: "border-red-500 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-950 dark:text-red-300",
          duration: 3000,
        });
      }
    } catch (error: any) {
      console.error("Toggle error:", error);
      // Revert optimistic update on error
      setGradeSchemes(prev => 
        prev.map(s => ({
          ...s,
          isActive: s.id === schemeId ? scheme.isActive : s.isActive
        }))
      );
      toast({
        title: "Toggle Failed",
        description: error.message || "Unable to update scheme status.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleEditScheme = (scheme: GradeScheme) => {
    setEditingScheme(scheme);
    setIsFormOpen(true);
  };



  return (
    <div className="space-y-2 m-2">
      <ContentLoader isLoading={isAuthLoading || !hasValidClaims} message="">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4 m-2">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold">Grading Structure</h3>
          <p className="text-sm text-muted-foreground">Customize grade boundaries and scales</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)} className="w-full md:w-auto bg-[#3879E3] hover:bg-[#306ECD] text-white">
          <Plus className="w-4 h-4 mr-2" />
          New Scheme
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
        {gradeSchemes.map((scheme) => (
          <Card key={scheme.id} className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base md:text-lg">{scheme.name}</CardTitle>
                <div className="flex items-center gap-3">
                  {scheme.isActive && <Badge variant="default" className="text-xs bg-green-500 hover:bg-green-500">Active</Badge>}
                  <Button size="sm" variant="outline" onClick={() => handleEditScheme(scheme)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                <span className="text-muted-foreground">Type:</span>
                <span className="capitalize">{scheme.type}</span>
                <span className="text-muted-foreground">Pass Threshold:</span>
                <span>{scheme.passThreshold}%</span>
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-2">Grade Boundaries</h4>
                <div className="space-y-1">
                  {scheme.boundaries.map((boundary, index) => (
                    <div key={index} className="flex justify-between items-center text-xs md:text-sm p-2  rounded border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <Badge className={`${getGradeColor(boundary.grade)} px-2 py-1 text-xs mr-2`}>{boundary.grade}</Badge>
                      <span>{boundary.min} - {boundary.max}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={scheme.isActive}
                    onCheckedChange={() => toggleActiveScheme(scheme.id)}
                    className={scheme.isActive ? "data-[state=checked]:bg-green-500" : "data-[state=unchecked]:bg-gray-300"}
                  />
                  <Label className={`text-sm ${scheme.isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>Active</Label>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleEditScheme(scheme)}>
                  <Settings className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <GradeSchemeForm 
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingScheme(undefined);
        }}
        onSave={handleSaveScheme}
        onDelete={handleDeleteScheme}
        editingScheme={editingScheme}
        schoolID={schoolID}
        submittedBy={submittedBy}
      />
      </ContentLoader>
    </div>
  );
};

export default GradingStructure;